import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';

import {api} from 'app/modules/Remote';

import {ApiSavedPrompt, SavedPromptDraft} from './types';

// The API's cap on a saved prompt's text (MAX_PORTFOLIO_PROMPT_LENGTH in the agent blueprint).
export const MAX_SAVED_PROMPT_LENGTH = 10_000;

const SAVED_PROMPTS_QUERY_KEY = 'agent-saved-prompts';

// The API's page cap. One page holds an org's automations for now; the list has no "load more".
const PER_PAGE = 100;

export function useSavedPromptsQuery(
  organizationId: string,
  {enabled = true, archived = false} = {}
) {
  return useQuery({
    queryKey: [SAVED_PROMPTS_QUERY_KEY, organizationId, {archived}],
    queryFn: async () => {
      const response = await api.agent.listPrompts({perPage: PER_PAGE, archived});
      return response.get('data').toJS() as ApiSavedPrompt[];
    },
    enabled,
    retry: 1,
  });
}

export function useCreateSavedPrompt(organizationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (draft: SavedPromptDraft) => {
      const response = await api.agent.createPrompt({organizationId, ...draft});
      return response.get('data').toJS() as ApiSavedPrompt;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({queryKey: [SAVED_PROMPTS_QUERY_KEY, organizationId]}),
  });
}

/** Archives (`archived: true`) or restores a saved prompt, refetching both the active and archived lists. */
export function useSetSavedPromptArchived(organizationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({promptId, archived}: {promptId: number; archived: boolean}) => {
      const response = await (archived
        ? api.agent.archivePrompt(promptId)
        : api.agent.restorePrompt(promptId));
      return response.get('data').toJS() as ApiSavedPrompt;
    },
    onSettled: () =>
      queryClient.invalidateQueries({queryKey: [SAVED_PROMPTS_QUERY_KEY, organizationId]}),
  });
}
